import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireAdminOrTeacher?: boolean;
  requireEmployee?: boolean;
  requireTeacherOrEmployee?: boolean;
  requireClaim?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireAdminOrTeacher = false,
  requireEmployee = false,
  requireTeacherOrEmployee = false,
  requireClaim,
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const [claims, setClaims] = useState<string[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(true);

  useEffect(() => {
    const fetchClaims = async () => {
      // Always fetch claims for employees
      if (user?.role === "EMPLOYEE" && user?.id) {
        try {
          const response = await axios.get(`/admin-claims/employee/${user.id}`);
          if (response.data.success) {
            setClaims(response.data.data.claimCodes || []);
          }
        } catch (error) {
          console.error("Failed to fetch claims:", error);
          setClaims([]);
        }
      }
      setClaimsLoading(false);
    };

    if (!loading) {
      fetchClaims();
    }
  }, [user, loading]);

  if (loading || (user?.role === "EMPLOYEE" && claimsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Role-based checks with claim support for employees
  if (requireAdmin) {
    // Admin has full access
    if (user?.role === "ADMIN") {
      return <>{children}</>;
    }
    // Employee needs the specific claim
    if (
      user?.role === "EMPLOYEE" &&
      requireClaim &&
      claims.includes(requireClaim)
    ) {
      return <>{children}</>;
    }
    // No access for others
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAdminOrTeacher) {
    // Admin and Teacher have access
    if (user?.role === "ADMIN" || user?.role === "TEACHER") {
      return <>{children}</>;
    }
    // Employee needs the specific claim
    if (
      user?.role === "EMPLOYEE" &&
      requireClaim &&
      claims.includes(requireClaim)
    ) {
      return <>{children}</>;
    }
    // No access for others
    return <Navigate to="/dashboard" replace />;
  }

  if (requireEmployee && user?.role !== "EMPLOYEE" && user?.role !== "ADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    requireTeacherOrEmployee &&
    user?.role !== "TEACHER" &&
    user?.role !== "EMPLOYEE" &&
    user?.role !== "ADMIN"
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
